import { useContext, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import fileExtension from 'file-extension';

import { showModal } from 'actions/modal';
import { updateTask, addTaskDoc, removeTaskDoc } from 'actions/tasks';
import { removeFile } from 'actions/files';
import { externalLoad } from 'api/files-api';

import { isPersonalTask } from 'utils/task';
import { isFileImage } from 'utils/file';
import { useCanUser } from 'shared-hooks/permission';
import { useProject } from 'shared-hooks/project';
import { trackEvent } from 'services/events-tracker-service';
import { createAlert } from 'services/alert-service';
import { GOOGLE_MIME_TYPES } from 'constants/index';

import { PublicPageContext } from 'context/PublicPageContext';

import DocContainer from 'containers/doc-container';

import AttachmentThumb from 'components/attachment-thumb';
import CanUser from 'components/CanUser';
import MultiUpload from 'components/multi-upload';
import DropPanel from 'components/drop-panel';
import DocCard from 'components/doc-card';
import TaskEmbed from 'components/TaskEmbed';

import IconFile from 'icons/icon-file';

const getBannerFromAttachments = attachments => {
    if (!attachments) {
        return null;
    }

    const attachment = attachments.find(attachment => isFileImage(attachment.name));
    return attachment ? attachment.url : null;
}

const AttachmentItem = ({ taskId, attachment, attachments, noRedirect, projectId }) => {
    const canEditTask = useCanUser('editTask');
    const publicPageCtx = useContext(PublicPageContext);
    const isPublic = publicPageCtx?.isPublic === true;

    const handleBannerUpdate = attachment => {
        updateTask(taskId, {
            banner: attachment.thumbnail
        });
    }

    const handleFileDelete = attachmentId => {
        trackEvent('FILE.DELETE', {
            via: 'Task',
            fileName: attachment?.name
        });

        const filteredAttachments = attachments.filter(attachment => attachment && attachment.id !== attachmentId);

        updateTask(taskId, {
            attachments: filteredAttachments,
            banner: getBannerFromAttachments(filteredAttachments)
        }, true);

        removeFile(attachmentId);
    }

    return (
        <AttachmentThumb
            {...attachment}
            noRedirect={noRedirect}
            filePreviewPath={`/${projectId}/task/${taskId}/files`}
            association="Task"
            withUtils={!isPublic}
            withComment={!isPublic}
            siblings={attachments.map(({ id }) => id)}
            onBannerUpdate={canEditTask && handleBannerUpdate}
            onDelete={handleFileDelete}
        />
    );
}

const TaskAttachments = ({ id, task, projectId, noRedirect, banner, attachments, documents }) => {
    const { t } = useTranslation(['task', 'common', 'file'])
    const publicPageCtx = useContext(PublicPageContext);
    const isPublic = publicPageCtx?.isPublic === true;
    const isPersonal = isPersonalTask(task);
    const canRemoveFile = useCanUser('removeFile');
    const [addingEmbed, setAddingEmbed] = useState(false);
    const project = useProject(projectId);

    const handleEmbedAddClick = () => {
        setAddingEmbed(true);
    }

    const handleEmbedRemove = () => {
        setAddingEmbed(false);
    }


    const handleUpload = newAttachments => {
        if (!newAttachments) {
            return;
        }

        if (!banner) {
            const newBanner = getBannerFromAttachments(newAttachments);

            if (newBanner) {
                updateTask(id, {
                    banner: newBanner
                }, true)
            }
        }

        newAttachments.forEach(file => {
            trackEvent('FILE.UPLOAD', {
                via: 'Task',
                id: file.id,
                fileType: fileExtension(file.name),
                fileSize: file.size
            });
        });
    }

    const handleInternalDocSelect = docId => {
        addTaskDoc(id, docId);
    }

    const handleExternalFilesSelect = files => {
        if (!files.length) {
            return;
        }

        let filesToUpload;

        if (files[0]?.serviceType === 'google') {
            const docFiles = files.filter(file => GOOGLE_MIME_TYPES.includes(file.mimeType));
            filesToUpload = files.filter(file => !GOOGLE_MIME_TYPES.includes(file.mimeType));

            if (docFiles.length) {
                showModal('GOOGLE_DOC_CREATE', {
                    projectId,
                    folderId: project?.doc_root_folder,
                    rootFolderId: project?.doc_root_folder,
                    docs: docFiles,
                    navigateOnSuccess: false,
                    onCreate: handleInternalDocSelect
                });
            }
        } else {
            filesToUpload = files;
        }

        if (filesToUpload.length) {
            const alert = createAlert('success', <><i className="icon material-icons icon-animate-rotate">autorenew</i> { t('file:importingFile', { count: files.length, name: files[0].name } )}</>, {
                duration: 0
            });

            externalLoad({
                project: projectId,
                task: id,
                files: filesToUpload.map(({ serviceProps }) => serviceProps)
            }).then(response => {
                alert.setContent(
                    <>
                        <i className="icon material-icons icon-animate-rotate">autorenew</i> { t('file:importedFile', { count: filesToUpload.length }) }
                    </>
                );
                alert.hide(5000);
            });
        }
    }

    const handleDocRemove = docId => {
        removeTaskDoc(id, docId);
    }

    if (isPersonal || publicPageCtx?.pageConfig?.tasks?.show_attachments === false) {
        return null;
    }

    return (
        <div className="content-panel-section section-files" data-section="files">
            <div className="section-head">
                <h4>
                    <span className="title-icon"><IconFile /></span>

                    <span className="text">{ t('common:Attachments') }</span>

                    <CanUser type="addFile">
                        <MultiUpload
                            taskId={id}
                            projectId={projectId}
                            internalDocEnabled={true}
                            onEmbedAdd={handleEmbedAddClick}
                            btnClassName="btn-colored btn-circle btn-xxsmall btn-simple btn-icon"
                            onUpload={handleUpload}
                            onInternalDocSelect={handleInternalDocSelect}
                            onExternalFilesSelect={projectId && handleExternalFilesSelect}
                        >
                            +
                        </MultiUpload>
                    </CanUser>
                </h4>
            </div>

            <div className="section-body">
                <div className="content-panel-files">
                    <CanUser type="addFile">
                        <DropPanel
                            taskId={id}
                            upload={true}
                            className="drop-panel-default drop-panel-inline drop-panel-small"
                            onUpload={handleUpload}
                        >
                            <span>
                                <i className="icon ion ion-android-attach" />

                                <Trans i18nKey="file:Drag & drop or choose files" components={{ u: <u /> }} />
                            </span>
                        </DropPanel>
                    </CanUser>

                    {
                        (attachments || []).map(attachment => (
                            <AttachmentItem
                                key={attachment.id}
                                taskId={id}
                                attachment={attachment}
                                attachments={attachments}
                                noRedirect={noRedirect}
                                projectId={projectId}
                            />
                        ))
                    }

                    {
                        !isPublic && (documents || []).map(docId => (
                            <DocContainer key={docId} id={docId}>
                                <DocCard
                                    removeTooltip={t('Remove From Task')}
                                    onRemove={canRemoveFile && handleDocRemove}
                                />
                            </DocContainer>
                        ))
                    }

                    {
                        task.embed_url || addingEmbed ? <TaskEmbed id={task.id} onRemove={handleEmbedRemove} /> : null
                    }
                </div>
            </div>
        </div>
    );
}

export default TaskAttachments
